import {maybe} from '@freckle/maybe'
import {localePercentWhole} from '@freckle/educator-entities/ts/common/helpers/number'
import type {QuestionSharedT} from '@freckle/educator-entities/ts/common/logic/school-assessment-report'
import {
  groupByQuestionId,
  getSharedQuestions
} from '@freckle/educator-entities/ts/common/logic/school-assessment-report'
import {
  toHTMLTextDangerous,
  innerHTMLText
} from '@freckle/educator-entities/ts/common/models/html-text'
import type {RlStandardWithDomainT} from '@freckle/educator-entities/ts/common/models/rl-standard'
import type {AssessmentAnswerT} from '@freckle/educator-entities/ts/math/assessment/models/assessment-answer'
import type {MathQuestionMapT} from '@freckle/educator-entities/ts/math/common/models/questions'
import {EnableMathJax} from '@freckle/educator-materials/ts/components/enable-math-jax'
import {H2} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {CorrectAnswersForReport} from '@freckle/educator-materials/ts/math/common/components/correct-answers'
import {table, tableBordered} from '../table.module.scss'
import {questionImage} from './assessment-questions-report.module.scss'

type Props = {
  assessmentAnswers: Array<AssessmentAnswerT>
  questionMap: MathQuestionMapT
  rlStandards: RlStandardWithDomainT[]
}

function Row(props: {sharedQuestion: QuestionSharedT}) {
  const {sharedQuestion} = props
  const {t} = useSafeTranslation()
  const accuracyText = maybe(
    () => t('COMMON_NON_APPLICABLE'),
    accuracy => localePercentWhole(Math.round(accuracy)),
    sharedQuestion.accuracy
  )

  const imageNode =
    sharedQuestion.questionImageUrl !== null && sharedQuestion.questionImageUrl !== undefined ? (
      <a href={sharedQuestion.questionImageUrl}>
        <img
          src={sharedQuestion.questionImageUrl}
          alt={t('ASSESSMENT_REPORT_QUESTIONS_IMAGE_TITLE', {
            questionNumber: sharedQuestion.questionIndex
          })}
          title={t('ASSESSMENT_REPORT_QUESTIONS_IMAGE_TITLE', {
            questionNumber: sharedQuestion.questionIndex
          })}
          className={questionImage}
        />
      </a>
    ) : null

  return (
    <tr>
      <td>{sharedQuestion.questionIndex}</td>
      <td>{sharedQuestion.standardCode}</td>
      <td>
        {imageNode}
        <span {...innerHTMLText(toHTMLTextDangerous(sharedQuestion.questionText))} />
      </td>
      <td>
        <CorrectAnswersForReport question={sharedQuestion.questionCorrectAnswers} />
      </td>
      <td>{sharedQuestion.numQuestionAnswered}</td>
      <td>{accuracyText}</td>
    </tr>
  )
}

export function AssessmentQuestionsReport(props: Props) {
  const {assessmentAnswers, questionMap, rlStandards} = props
  const {t} = useSafeTranslation()

  const groupedByQuestionId = groupByQuestionId(assessmentAnswers, rlStandards)
  const sharedQuestions = getSharedQuestions(groupedByQuestionId, questionMap)

  return (
    <EnableMathJax>
      <div id="question-list">
        <H2>{t('ASSESSMENT_REPORT_QUESTIONS_SECTION_TITLE')}</H2>

        <table className={`${table} ${tableBordered}`}>
          <thead>
            <tr>
              <th>#</th>
              <th>{t('ASSESSMENT_REPORT_QUESTIONS_STANDARD_COLUMN_TITLE')}</th>
              <th>{t('ASSESSMENT_REPORT_QUESTIONS_QUESTION_COLUMN_TITLE')}</th>
              <th>{t('ASSESSMENT_REPORT_QUESTIONS_ANSWER_COLUMN_TITLE')}</th>
              <th>{t('ASSESSMENT_REPORT_QUESTIONS_NUM_OF_QUESSTIONS_COLUMN_TITLE')}</th>
              <th>{t('ASSESSMENT_REPORT_QUESTIONS_ACCURACY_COLUMN_TITLE')}</th>
            </tr>
          </thead>
          <tbody>
            {sharedQuestions.map(question => (
              <Row sharedQuestion={question} key={question.questionId} />
            ))}
          </tbody>
        </table>
      </div>
    </EnableMathJax>
  )
}
